import React, { useContext, useEffect, useState } from 'react';
import { Building2 } from 'lucide-react';

import { warehouseApi } from 'api/warehouse';
import { UserContext } from 'contexts/UserContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shadcn/components/ui/Select';

/**
 * Facility picker component for selecting the active facility
 */
export function FacilityPicker({ onChange }) {
    const { user } = useContext(UserContext);
    const [selectedFacility, setSelectedFacility] = useState(null);
    const [facilityOptions, setFacilityOptions] = useState([]);

    const getFacilityOptions = async () => {
        const res = await warehouseApi.getFacilities(true);
        if (res.status !== true) {
            return;
        }

        // Filters out freezer facilities
        const packingFacilities = res.data.rows.filter((item) => item.packingFacility);

        const capitalize = (str) => (str ? str[0].toUpperCase() + str.slice(1) : '');
        const optionsWithLabels = packingFacilities.map((item) => ({
            value: item.id,
            label: `${item.name} - ${capitalize(item.cluster)}`,
            name: item.name,
        }));

        const allOptions = [
            { value: 'ALL', label: 'ALL - All Facilities', name: 'All Facilities' },
            ...optionsWithLabels,
        ];
        setFacilityOptions(allOptions);

        // Set default to user's facility or first option
        const defaultFacility = allOptions.find((f) => f.value === user?.facilityID) || allOptions[0];
        if (defaultFacility) {
            setSelectedFacility(defaultFacility);
        }
    };

    const handleValueChange = (value) => {
        const facility = facilityOptions.find((f) => f.value === value);
        if (facility) {
            setSelectedFacility(facility);
        }
    };

    useEffect(() => {
        if (selectedFacility && onChange) {
            onChange(selectedFacility);
        }
    }, [selectedFacility, onChange]);

    useEffect(() => {
        getFacilityOptions();
    }, []);

    return (
        <div className="bg-white rounded-lg border-1 shadow-sm px-4 py-2">
            <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Facility:</span>
                <Select value={selectedFacility?.value || ''} onValueChange={handleValueChange}>
                    <SelectTrigger className="w-[200px] bg-stone-50 border-1 shadow-none">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {facilityOptions.map((facility) => (
                            <SelectItem key={facility.value} value={facility.value}>
                                {facility.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
        </div>
    );
}
